import { graphql } from 'react-relay';

/**
 * The Home feed inspector's picker directory (PRD pv-platform#156, slice 2): whether the Home feed is on
 * for the household, and its members and Searches. The lists come back even with the flag off. An unknown
 * vault is a `NOT_FOUND` error. Named operators only; recorded against the operator server-side.
 */
export const homeFeedHouseholdQuery = graphql`
    query homeFeedQueriesHouseholdQuery($vaultId: UUID!) {
        homeFeedHousehold(vaultId: $vaultId) {
            vaultId
            homeFeedOn
            members {
                accountId
                role
                joinedUtc
            }
            searches {
                searchId
                name
                stage
                isDefault
            }
        }
    }
`;

/**
 * One dry-run inspection of one member's view of one Search, evaluated at the server's "now". Read-only;
 * a vault, Search or member that is not in the household is a `NOT_FOUND` error. `preferences` and `questions` are null with the flag off. `INSPECTION_BUSY` when another inspection is running. Later slices add sections.
 */
export const homeFeedInspectionQuery = graphql`
    query homeFeedQueriesInspectionQuery($vaultId: UUID!, $searchId: UUID!, $memberAccountId: UUID!) {
        homeFeedInspection(vaultId: $vaultId, searchId: $searchId, memberAccountId: $memberAccountId) {
            vaultId
            searchId
            memberAccountId
            evaluatedAt
            homeFeedOn
            inferences {
                outcome
                interestedHomes
                engagedHomes
                minInterestedHomes
                countdown {
                    lastDecisionUtc
                    lastDecisionKey
                    lastDecisionKind
                    lastDecisionIsWidening
                    cooldownHomes
                    homesSinceLastDecision
                    moreHomes
                    offerableNow
                }
                onOffer {
                    key
                    suggestion
                    evidence
                    skippedEvidence
                    matching
                    interested
                    homes {
                        indexedListingId
                        address
                        matches
                    }
                }
                candidates {
                    key
                    suggestion
                    evidence
                    status
                }
                rejections {
                    key
                    rejectedUtc
                    mayReturnUtc
                    stillQuiet
                }
                dismissals {
                    key
                    dismissedUtc
                }
                confirmations {
                    key
                    confirmedUtc
                }
            }
            widening {
                outcome
                holdingInferenceKey
                interestedWildcards
                minInterestedWildcards
                countdown {
                    lastDecisionUtc
                    lastDecisionKey
                    lastDecisionKind
                    lastDecisionIsWidening
                    cooldownHomes
                    homesSinceLastDecision
                    moreHomes
                    offerableNow
                }
                onOffer {
                    key
                    axis
                    suggestion
                    evidence
                    interested
                    shown
                    homes {
                        indexedListingId
                        address
                    }
                }
                axes {
                    axis
                    status
                    interested
                    skipped
                }
                candidates {
                    key
                    axis
                    suggestion
                    evidence
                    status
                }
                rejections {
                    key
                    rejectedUtc
                    mayReturnUtc
                    stillQuiet
                }
                dismissals {
                    key
                    dismissedUtc
                }
                confirmations {
                    key
                    confirmedUtc
                }
                pendingAgreements {
                    waitingOn
                    wideningKey
                    kind
                    subject
                    value
                    statedUtc
                }
            }
            questions {
                todayReason
                todayUntil
                scanDays
                snoozedUntil
                pickedOnNextQuestionDay
                nextDay {
                    day
                    from
                    homesBefore
                    prefersNonRanking
                }
                picked {
                    key
                    kind
                    prompt
                    poolSize
                    mostReordered
                    totalReordered
                    afterHomes
                    todayPrefersNonRanking
                    answers {
                        label
                        reordered
                    }
                }
                others {
                    key
                    kind
                    prompt
                    mostReordered
                    totalReordered
                }
                latestAnswers {
                    key
                    value
                    answeredUtc
                }
            }
            wildcards {
                planState
                notPlannedReason
                pacing {
                    timesShown
                    lookbackDays
                    rate
                    oneIn
                    baseRate
                    rateState
                    activeAxes
                    rateWindow
                    paused {
                        axis
                        untilUtc
                    }
                    recent {
                        axis
                        skipped
                        atUtc
                    }
                }
                axesOff {
                    axis
                    source
                    sinceUtc
                }
                priceFirm {
                    isFirm
                    answerKey
                    answer
                    answeredUtc
                }
            }
            resultsChanged {
                pending
                readWouldWrite
                card {
                    title
                    movers {
                        listingId
                        address
                        direction
                        rank
                        previousRank
                        places
                        score
                        reason
                        fitBand
                    }
                }
                state
                baselineMovedUtc
                cardNotedSinceUtc
                cardLapsesUtc
                preferencesChangedSinceBaseline
            }
            engagement {
                windowDays
                cap
                truncated
                events {
                    kind
                    occurredUtc
                    listingId
                    quantity
                    wildcardAxis
                }
            }
            preferences {
                items {
                    kind
                    subject
                    value
                    source
                    householdStatus
                    statedAt
                    overriddenByAccountId
                }
                household {
                    held {
                        kind
                        subject
                        value
                        memberAccountIds
                    }
                    pending {
                        kind
                        subject
                        householdValue
                        views {
                            memberAccountId
                            value
                            householdStatus
                            overriddenByAccountId
                        }
                    }
                    conflicts {
                        kind
                        subject
                        householdValue
                        views {
                            memberAccountId
                            value
                            householdStatus
                            overriddenByAccountId
                        }
                    }
                }
            }
        }
    }
`;
